// src/components/customers-table.tsx

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  createMRTColumnHelper,
} from "material-react-table";

import { format, parseISO } from "date-fns";

import { Customer } from "src/api/types/customer.types";
import CustomerDetailsDrawer from "./customer-details-drawer";
import { paths } from "src/paths";

// Create column helper for strongly typed columns
const columnHelper = createMRTColumnHelper<Customer>();

const CustomersTable: React.FC<{
  data: Customer[];
  totalCount: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}> = ({ data, totalCount, page, limit, onPageChange, onLimitChange }) => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const columns = useMemo<MRT_ColumnDef<Customer, string>[]>(
    () => [
      columnHelper.accessor("user_id", {
        header: "User ID",
        size: 120,
        Cell: ({ cell }) => {
          const userId = cell.getValue<string>() ?? "-";
          const truncated = userId.slice(0, 8);
          return (
            <Tooltip title="Click to copy" arrow>
              <Typography
                component="span"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: "primary.main",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(userId);
                  setCopySuccess(true);
                }}
              >
                {truncated}
              </Typography>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor("email", {
        header: "Email",
        size: 250,
      }),
      columnHelper.accessor("mobile", {
        header: "Mobile",
        size: 150,
      }),
      columnHelper.accessor("created_at", {
        header: "Created At",
        size: 130,
        Cell: ({ cell }) => {
          const dateStr = cell.getValue<string>();
          try {
            return format(parseISO(dateStr), "dd/MM/yyyy");
          } catch {
            return dateStr || "-";
          }
        },
      }),
      columnHelper.accessor("client_id", {
        header: "Client ID",
        size: 120,
        Cell: ({ cell }) => {
          const clientId = cell.getValue<string>() ?? "-";
          return (
            <Tooltip title="Click to copy" arrow>
              <Typography
                component="span"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: "primary.main",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 100,
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(clientId);
                  setCopySuccess(true);
                }}
              >
                {clientId}
              </Typography>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor("user_type", {
        header: "User Type",
        size: 120,
      }),
    ],
    []
  );

  const onRowClick = (row: MRT_Row<Customer>) => {
    navigate(
      paths.dashboard.customers.details.replace(":id", row.original.user_id)
    );
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    if (page !== page) {
      onPageChange(page);
    }
    if (pageSize !== limit) {
      onLimitChange(pageSize);
    }
  };

  return (
    <>
      <MaterialReactTable
        columns={columns}
        data={data}
        manualPagination
        enableSorting
        enableColumnFilters
        pageCount={Math.ceil(totalCount / limit)}
        state={{
          pagination: { pageIndex: page - 1, pageSize: limit },
        }}
        onPaginationChange={(updater) => {
          const newState =
            typeof updater === "function"
              ? updater({ pageIndex: page - 1, pageSize: limit })
              : updater;
          handlePaginationChange(newState.pageIndex + 1, newState.pageSize);
        }}
        muiTablePaperProps={{
          sx: {
            width: "100%",
            backGroundColor: "gray",
          },
        }}
        muiTableContainerProps={{
          sx: {
            width: "100%",
          },
        }}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => onRowClick(row),
          sx: { cursor: "pointer" },
        })}
        positionToolbarAlertBanner="bottom"
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", gap: 0, p: 0 }}>
            <Button
              onClick={() => {
                const csvContent = data.map((d) => ({
                  user_id: d.user_id,
                  email: d.email,
                  mobile: d.mobile,
                  created_at: d.created_at,
                  client_id: d.client_id,
                  user_type: d.user_type,
                }));
                const csv = [
                  Object.keys(csvContent[0]).join(","),
                  ...csvContent.map((row) => Object.values(row).join(",")),
                ].join("\n");
                const blob = new Blob([csv], {
                  type: "text/csv;charset=utf-8;",
                });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.setAttribute("download", "customers.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              startIcon={<FileDownloadIcon />}
             
            >
              Export CSV
            </Button>
          </Box>
        )}
      />

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setCopySuccess(false)}>
          ID copied to clipboard!
        </Alert>
      </Snackbar>

      <CustomerDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userId={selectedUserId}
      />
    </>
  );
};

export default CustomersTable;
